/*
 * Design: Swiss Precision × 和の余白
 * 4業種業態別のソリューション提案 + 各ソリューション直後にCTA
 * Mobile: タブ横スクロール、カード余白最適化、タッチターゲット48px
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Factory,
  Truck,
  Briefcase,
  ShoppingBag,
  ArrowRight,
  CheckCircle2,
  Cpu,
  BarChart3,
  ClipboardList,
  Scan,
  Package,
  FileText,
  Users,
  Calendar,
  Headphones,
  MapPin,
  Globe,
  TrendingUp,
} from "lucide-react";

const INDUSTRIES = [
  {
    id: "manufacturing",
    icon: Factory,
    label: "製造業",
    color: "#E03030",
    heroTitle: "製造現場の「見えない」を「見える」に変える",
    heroDescription:
      "RFID在庫管理から作業工程管理まで、製造業の現場課題をAIエージェントとIoTで一気通貫に解決。属人化した業務をデジタルで標準化し、品質・生産性・トレーサビリティを同時に向上させます。",
    solutions: [
      {
        icon: Scan,
        title: "RFID在庫・資材管理",
        description:
          "RFIDタグとAIエージェントの連携で、資材・仕掛品・完成品の在庫をリアルタイムに把握。棚卸作業を従来の1/10の時間に短縮し、在庫精度99%以上を実現します。",
        results: ["棚卸時間 90%削減", "在庫精度 99%以上", "欠品・過剰在庫の解消"],
      },
      {
        icon: ClipboardList,
        title: "作業工程管理システム",
        description:
          "各工程の進捗・品質データをリアルタイムで収集・可視化。AIが異常検知とボトルネック分析を自動で行い、生産計画の最適化を支援します。",
        results: ["工程進捗のリアルタイム把握", "不良率 30%低減", "生産リードタイム短縮"],
      },
      {
        icon: Cpu,
        title: "設備稼働モニタリング",
        description:
          "IoTセンサーとAIで設備の稼働状況を常時監視。異常の予兆を検知し、計画外停止を未然に防止。メンテナンスコストの最適化を実現します。",
        results: ["計画外停止 50%削減", "設備稼働率の向上", "メンテナンスコスト最適化"],
      },
      {
        icon: BarChart3,
        title: "品質トレーサビリティ",
        description:
          "原材料の入荷から製品出荷まで、全工程のデータを自動記録。不良発生時の原因追跡を数分で完了し、リコール対応コストを大幅に削減します。",
        results: ["原因追跡 数分で完了", "品質記録の自動化", "顧客信頼の向上"],
      },
    ],
  },
  {
    id: "wholesale",
    icon: Truck,
    label: "卸売・物流業",
    color: "#2563EB",
    heroTitle: "荷受から請求まで、業務の流れを止めない",
    heroDescription:
      "荷受・検品・在庫・出荷・請求——分断されがちな業務プロセスをAIエージェントが一気通貫でつなぎます。RFID棚卸と自動伝票処理で、人手不足の現場でも正確かつ迅速なオペレーションを実現します。",
    solutions: [
      {
        icon: Package,
        title: "RFID棚卸・在庫の見える化",
        description:
          "倉庫内の全商品をRFIDで一括読み取り。AIが在庫データをリアルタイムで更新し、適正在庫の維持と発注タイミングの最適化を自動で行います。",
        results: ["棚卸時間 95%削減", "在庫回転率の改善", "発注の自動最適化"],
      },
      {
        icon: FileText,
        title: "受発注・請求書の自動処理",
        description:
          "FAX・メール・EDIで届く注文データをAIが自動で読み取り・仕分け。請求書の作成から送付まで、月末の事務作業を大幅に効率化します。",
        results: ["伝票処理時間 80%削減", "入力ミスの解消", "月末業務の平準化"],
      },
      {
        icon: Truck,
        title: "配送ルート最適化",
        description:
          "配送先・荷量・時間帯をAIが分析し、最適な配送ルートと積載計画を自動生成。燃料コストの削減と配送品質の向上を同時に実現します。",
        results: ["配送コスト 15%削減", "配送時間の短縮", "ドライバー負担の軽減"],
      },
      {
        icon: BarChart3,
        title: "取引データ分析・BI",
        description:
          "取引先別・商品別の売上・利益データをダッシュボードで可視化。AIが需要予測と価格最適化の提案を行い、データドリブンな経営判断を支援します。",
        results: ["売上分析の自動化", "需要予測の精度向上", "利益率の改善"],
      },
    ],
  },
  {
    id: "service",
    icon: Briefcase,
    label: "サービス業",
    color: "#059669",
    heroTitle: "現場スタッフの「手間」を減らし、「接客」に集中できる環境を",
    heroDescription:
      "予約管理、シフト調整、顧客対応記録、報告書作成——サービス業の現場を圧迫する間接業務をAIエージェントが代行。スタッフが本来の「おもてなし」に集中できる環境を構築します。",
    solutions: [
      {
        icon: Users,
        title: "顧客管理・フォロー自動化",
        description:
          "来店・問い合わせ・購買履歴をAIが自動で統合管理。フォローアップのタイミングと内容をAIが提案し、リピート率と顧客満足度を向上させます。",
        results: ["フォロー漏れゼロ", "リピート率 20%向上", "顧客対応の標準化"],
      },
      {
        icon: Calendar,
        title: "予約・シフト管理の最適化",
        description:
          "予約状況・スタッフスキル・繁閑データをAIが分析し、最適なシフトを自動生成。予約変更やキャンセルにも即座に対応し、稼働率を最大化します。",
        results: ["シフト作成時間 90%削減", "稼働率の最大化", "人件費の最適化"],
      },
      {
        icon: Headphones,
        title: "問い合わせ対応の自動化",
        description:
          "電話・メール・チャットでの問い合わせをAIエージェントが一次対応。よくある質問は即座に回答し、複雑な案件のみスタッフに振り分けます。",
        results: ["一次対応の自動化 60%", "応答時間の短縮", "スタッフ負担の軽減"],
      },
      {
        icon: FileText,
        title: "日報・報告書の自動生成",
        description:
          "業務データから日報・週報・月次レポートをAIが自動生成。管理者は報告書作成に費やしていた時間を、現場改善やスタッフ育成に充てられます。",
        results: ["報告書作成時間 80%削減", "データの正確性向上", "管理業務の効率化"],
      },
    ],
  },
  {
    id: "retail",
    icon: ShoppingBag,
    label: "小売・観光業",
    color: "#D97706",
    heroTitle: "データで「売れる」を見える化し、体験価値を最大化する",
    heroDescription:
      "POSデータ・来客数・在庫状況をAIが統合分析し、売れ筋予測と在庫最適化を自動化。観光業では多言語対応とコンテンツ×データ活用で、インバウンド需要の取り込みを加速します。",
    solutions: [
      {
        icon: TrendingUp,
        title: "売上・在庫の見える化",
        description:
          "POSデータと在庫情報をリアルタイムで連携。AIが売れ筋・死に筋を自動分析し、発注量と陳列の最適化を提案。機会損失と廃棄ロスを同時に削減します。",
        results: ["在庫回転率 25%改善", "廃棄ロス削減", "売上予測の精度向上"],
      },
      {
        icon: Globe,
        title: "多言語AIコンシェルジュ",
        description:
          "AIエージェントが多言語で接客・案内・予約対応を実施。スタッフの語学力に依存せず、インバウンド観光客への対応品質を均一化します。",
        results: ["多言語対応の自動化", "接客品質の均一化", "インバウンド売上向上"],
      },
      {
        icon: MapPin,
        title: "来客分析・動線最適化",
        description:
          "センサーとAIで来客数・滞在時間・動線を分析。混雑予測に基づくスタッフ配置の最適化と、売場レイアウトの改善を支援します。",
        results: ["来客データの自動収集", "スタッフ配置の最適化", "売場効率の向上"],
      },
      {
        icon: BarChart3,
        title: "コンテンツ×データ活用",
        description:
          "SNS・口コミ・予約データをAIが分析し、効果的なプロモーション施策を自動提案。データに基づくコンテンツ配信で集客力を強化します。",
        results: ["集客コスト削減", "SNS効果の可視化", "リピーター率の向上"],
      },
    ],
  },
];

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export function IndustrySolutionsSection() {
  const [activeTab, setActiveTab] = useState("manufacturing");
  const activeIndustry = INDUSTRIES.find((ind) => ind.id === activeTab)!;

  return (
    <section id="solutions" className="py-14 sm:py-20 lg:py-28 bg-[#F9F9F9]">
      <div className="container">
        {/* Section Header */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="mb-8 sm:mb-12">
          <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="w-1.5 sm:w-2 h-7 sm:h-8 bg-[#E03030]" />
            <span
              className="text-[10px] sm:text-xs font-medium tracking-widest uppercase text-[#666666]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Industry Solutions
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-[#1A1A1A] mb-3 sm:mb-4">
            業種別ソリューション
          </h2>
          <p className="text-sm sm:text-base text-[#333333] max-w-3xl leading-relaxed">
            81合同会社は、業種ごとの商習慣と現場の実態を熟知しています。
            Antigravity × Workspace MCPの技術を、御社の業種に最適化してご提案します。
          </p>
        </motion.div>

        {/* Industry Tabs - Horizontal scroll on mobile */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6, delay: 0.1 }} className="mb-8 sm:mb-10">
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap scrollbar-hide">
            {INDUSTRIES.map((industry) => (
              <button
                key={industry.id}
                onClick={() => setActiveTab(industry.id)}
                className={`inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 sm:py-3 text-sm font-bold transition-all duration-300 border shrink-0 touch-manipulation ${
                  activeTab === industry.id
                    ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                    : "bg-white text-[#333333] border-[#E0E0E0] hover:border-[#1A1A1A]"
                }`}
              >
                <industry.icon size={18} strokeWidth={1.5} />
                {industry.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Active Industry Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.4 }}
          >
            {/* Industry Hero */}
            <div className="bg-white border border-[#E0E0E0] p-5 sm:p-8 lg:p-12 mb-6 sm:mb-8">
              <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div
                  className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-sm text-white"
                  style={{ backgroundColor: activeIndustry.color }}
                >
                  <activeIndustry.icon size={20} strokeWidth={1.5} className="sm:hidden" />
                  <activeIndustry.icon size={24} strokeWidth={1.5} className="hidden sm:block" />
                </div>
                <div>
                  <span
                    className="text-[10px] sm:text-xs font-medium tracking-wider uppercase text-[#666666] mb-1 block"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {activeIndustry.label}
                  </span>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#1A1A1A]">
                    {activeIndustry.heroTitle}
                  </h3>
                </div>
              </div>
              <p className="text-sm sm:text-base text-[#333333] leading-relaxed max-w-4xl">
                {activeIndustry.heroDescription}
              </p>
            </div>

            {/* Solution Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {activeIndustry.solutions.map((solution, i) => (
                <motion.div
                  key={solution.title}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="group bg-white border border-[#E0E0E0] p-5 sm:p-7 hover:border-[#E03030] transition-all duration-300 hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)]"
                >
                  <div className="flex items-center gap-3 mb-3 sm:mb-4">
                    <div
                      className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-sm text-white"
                      style={{ backgroundColor: activeIndustry.color }}
                    >
                      <solution.icon size={16} strokeWidth={1.5} className="sm:hidden" />
                      <solution.icon size={18} strokeWidth={1.5} className="hidden sm:block" />
                    </div>
                    <h4 className="text-sm sm:text-base font-bold text-[#1A1A1A] group-hover:text-[#E03030] transition-colors">
                      {solution.title}
                    </h4>
                  </div>
                  <p className="text-xs sm:text-sm text-[#333333] leading-relaxed mb-3 sm:mb-4">
                    {solution.description}
                  </p>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {solution.results.map((result) => (
                      <span
                        key={result}
                        className="inline-flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs font-medium text-[#333333] bg-[#F9F9F9] px-2 sm:px-3 py-1 sm:py-1.5 border border-[#E0E0E0]"
                      >
                        <CheckCircle2 size={10} className="text-[#E03030] sm:hidden" />
                        <CheckCircle2 size={12} className="text-[#E03030] hidden sm:block" />
                        {result}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Per-Industry CTA */}
            <div className="bg-white border-2 border-[#E03030] p-5 sm:p-6 lg:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6">
              <div>
                <h4 className="text-base sm:text-lg font-bold text-[#1A1A1A] mb-1">
                  {activeIndustry.label}のDX、まずは無料で相談しませんか？
                </h4>
                <p className="text-xs sm:text-sm text-[#333333]">
                  御社の現場課題に合わせた具体的なソリューションをご提案します。
                </p>
              </div>
              <a
                href="#contact-form"
                className="shrink-0 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 bg-[#E03030] text-white font-bold text-sm rounded hover:bg-[#c72828] transition-colors duration-200 active:scale-[0.98] touch-manipulation"
              >
                無料相談を予約する
                <ArrowRight size={16} />
              </a>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
